import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { buildInvoiceHtmlDocument } from './vendorInvoiceHtml';

/**
 * Rasterize a DOM node to a multi-page A4 PDF and trigger download.
 */
export async function downloadInvoicePdfFromElement(element, filename = 'invoice.pdf') {
  if (!element) throw new Error('Nothing to export');

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  const imgData = canvas.toDataURL('image/png', 1.0);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(filename);
}

/**
 * Build standalone invoice HTML in an off-screen iframe and export to PDF (for list view without live preview).
 */
export async function downloadInvoicePdfFromInvoiceData(invoice, vendorProfile) {
  const html = buildInvoiceHtmlDocument(invoice, vendorProfile);
  const filename = `invoice-${invoice.invoice_number || invoice.id || 'export'}.pdf`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = 'invoice-pdf-export';
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:794px;min-height:1123px;border:0;margin:0;padding:0;opacity:0;pointer-events:none;background:#fff';

  document.body.appendChild(iframe);

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    iframe.onload = async () => {
      try {
        const doc = iframe.contentDocument;
        const body = doc?.body;
        if (!body) throw new Error('Invoice frame has no body');
        const root = doc?.querySelector('.invoice-sheet') || body;
        await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 100)));
        await downloadInvoicePdfFromElement(root, filename);
        resolve();
      } catch (e) {
        reject(e);
      } finally {
        cleanup();
      }
    };

    iframe.onerror = () => {
      cleanup();
      reject(new Error('Failed to load invoice for PDF'));
    };

    iframe.srcdoc = html;
  });
}
